import { Injectable } from '@angular/core';
import { HttpClient, HttpStatusCode } from '@angular/common/http';

import { SessionStore, UserInfo } from './session.store';
import { environment } from '@env/environment';
import { tap, catchError, map, take, mergeMap, shareReplay } from 'rxjs/operators';
import { throwError, Observable, of, from } from 'rxjs';
import { applyTransaction, resetStores } from '@datorama/akita';
import { Router } from '@angular/router';
import {
	LoginStatus,
	PasswordRegistrationResponse,
	PasswordRegistrationRequest,
	DefaultStatus
} from '@app-model/login';
import { CognitoUserSession, AuthenticationDetails } from 'amazon-cognito-identity-js';
import { ToastrService } from 'ngx-toastr';
import { AmplifyService } from '@app-services/amplify.service';
import { EmissionsControllerService } from '@app-services/emissions-controller.service';

@Injectable({
	providedIn: 'root'
})
export class SessionService {
	constructor(
		private sessionStore: SessionStore,
		private http: HttpClient,
		private amplifyService: AmplifyService,
		private router: Router,
		private toastr: ToastrService,
		private emissionsControllerService: EmissionsControllerService
	) {}

	setLoading(status: boolean) {
		this.sessionStore.setLoading(status);
	}

	checkCpfStatus(cpf: string): Observable<LoginStatus> {
		this.setLoading(true);
		return this.http.get(`${environment.api}/login/user/status/${cpf}`).pipe(
			map((response: LoginStatus) => response),
			tap(() => this.sessionStore.setLoading(false)),
			catchError(err => {
				this.setLoading(false);
				return throwError(err);
			})
		);
	}

	registerCompany(payload){

		this.setLoading(true);
		return this.http.post(`${environment.api}/login/production-company/register/`, payload).pipe(
			map((response : {status: HttpStatusCode}) => response),
			tap(() => this.sessionStore.setLoading(false)),
			catchError(err => {
				this.setLoading(false);
				return throwError(err);
			})
		)
	}

	createUser(email: string) {
		this.sessionStore.setLoading(true);
		return this.http.get(`${environment.api}/login/createUser/${email}`).pipe(
			take(1),
			map((response: PasswordRegistrationResponse) => response),
			tap(() => {
				this.sessionStore.setLoading(false);
			}),
			catchError(err => {
				this.setLoading(false);
				return throwError(err);
			})
		);
	}

	resetPassword(userId: string, verifCode: string, password: string): Observable<PasswordRegistrationResponse> {
		this.setLoading(true);
		const request: PasswordRegistrationRequest = {
			userId,
			verifCode,
			password
		};
		return this.http.post(`${environment.api}/login/user/reset-password`, request).pipe(
			map((response: PasswordRegistrationResponse) => response),
			tap(() => {
				this.sessionStore.setLoading(false);
			}),
			catchError(err => {
				this.setLoading(false);
				return throwError(err);
			})
		);
	}

	verifyUser(userId: string, verifCode: string): Observable<DefaultStatus> {
		return this.http
			.get(`${environment.api}/login/user/verify/${userId}/${verifCode}`)
			.pipe(map((response: DefaultStatus) => response));
	}

	confirmUserEmail(userId: string, verifCode: string): Observable<DefaultStatus> {
		return this.http.post(`${environment.api}/login/user/verify-email/${userId}/${verifCode}`, {},).pipe(
		map((response: DefaultStatus) => response));
	}

	forgotPassword(email: string) {
		this.setLoading(true);
		return this.http.get(`${environment.api}/login/user/reset-password/${email}`).pipe(
			take(1),
			tap(() => {
				this.sessionStore.setLoading(false);
			}),
			catchError(err => {
				this.setLoading(false);
				return throwError(err);
			})
		);
	}

	isAuthenticated(): Observable<boolean> {
		return from(this.amplifyService.isAuthenticated()).pipe(
			tap(isAuthenticated => {
				this.sessionStore.update({ isAuthenticated });
			})
		);
	}

	updateIsAuthenticated(isAuthenticated: boolean) {
		applyTransaction(() => {
			this.sessionStore.setLoading(false);
			this.sessionStore.update({ isAuthenticated });
		});
	}

	logout() {
		this.amplifyService.logout();
		this.sessionStore.clearSession();
		this.sessionStore.reset();
    resetStores();
		this.emissionsControllerService.clear();
		this.router.navigate(['/login']);
	}

	registerUser(userId: string, verifCode: string, values): Observable<PasswordRegistrationResponse> {
		return this.http.post(`${environment.api}/login/user/register/${userId}/${verifCode}`, values).pipe(
			take(1),
			map((response: PasswordRegistrationResponse) => response),
			tap(() => {
				this.sessionStore.setLoading(false);
			}),
			catchError(err => {
				this.setLoading(false);
				return throwError(err);
			})
		);
	}

	getUser(): Observable<UserInfo> {
		return this.sessionStore
			._select(state => state.userInfo)
			.pipe(
				mergeMap(userInfo => {
					if (userInfo) {
						return of(userInfo);
					}
					return this.getUserFromBack();
				})
			);
	}

	getUserFromBack(): Observable<UserInfo> {
		return this.http.get(`${environment.api}/user/info`).pipe(
			map((userInfo: UserInfo) => userInfo),
			tap(userInfo => this.sessionStore.update({ userInfo, currentCompany: {id: userInfo.selectedProductionCompany.id, name: userInfo.selectedProductionCompany.name} })),
			shareReplay()
		);
	}

	login(cpf: string, password: string, returnUrl?: string) {

		this.amplifyService.login(cpf, password).subscribe(
			response => {
				this.router.navigateByUrl(returnUrl ? returnUrl : '/');
				this.updateIsAuthenticated(true);
				this.getUser().subscribe();
			},
			err => {
				this.setLoading(false);
				const { code } = err;
				let message: string;
				if (code === 'NotAuthorizedException') {
					message = 'Usuário ou senha inválido';
				} else {
					message = 'Não foi possível realizar o seu login';
				}

				this.toastr.error('', message, {
					closeButton: true,
					progressBar: true,
					positionClass: 'toast-bottom-center',
					extendedTimeOut: 5000,
					timeOut: 10000
				});
			}
		);
	}
}
